import ClientContact from "../../../model/ClientContact"
import Client from "../../../model/Client"

import Box from "@mui/material/Box"
import { Card } from "../../styled-components/Card"
import TextField from "@mui/material/TextField"
import Chip from "@mui/material/Chip"
import ClientContactEntry from "./ClientContactEntry"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import ClientContactType from "../../../model/ClientContactType"


interface ClientFormProps {
    ogClient: Client;
    editClient: Client;
    setEditClient: (client: Client) => void;
    newContacts: ClientContact[];
    setNewContacts: (clientContacts: ClientContact[]) => void;
    setCanSave: (saveable: boolean) => void;
}


const EditClientForm: React.FC<ClientFormProps> = ({ogClient, editClient, setEditClient, newContacts, setNewContacts, setCanSave}) => {    
    

    const [newId, setNewId] = useState<number>(-1);
    const getNewContactId = () => {
        var id = newId;
        setNewId(newId - 1);
        return id;
    }

    useEffect(() => { 

        const hasValueChanged = () => {
            const val = editClient.name !== ogClient.name || 
            editClient.addressLine1 !== ogClient.addressLine1 ||
            editClient.addressLine2 !== ogClient.addressLine2 ||
            editClient.addressLine3 !== ogClient.addressLine3 ||
            editClient.postCode !== ogClient.postCode ||         
            editClient.abn !== ogClient.abn ||
            editClient.contacts.length !== ogClient.contacts.length ||
            newContacts.length > 0 ||
            contactChanged();
            return val;
        }

         const contactChanged = () => {        
            if(editClient.contacts.length === 0 && ogClient.contacts.length === 0){            
                return false;
            }
                
            const ogContactsMap = new Map(ogClient.contacts.map(c => [c.clientContactId, c]));
            
            for (const contact of editClient.contacts) {
                const ogContact = ogContactsMap.get(contact.clientContactId);

                // Check if the contact exists in original and if it has changed
                if (ogContact !== undefined && compareContact(contact, ogContact)) {
                    return true; // Found a changed contact
                }
            }
            
            return false;
        }

        const compareContact = (contact: ClientContact, otherContact: ClientContact) => {
            return contact.email !== otherContact.email ||
            contact.type !== otherContact.type ||
            contact.name !== otherContact.name ||
            contact.mobile !== otherContact.mobile ||
            contact.phone !== otherContact.phone
        }

        setCanSave(hasValueChanged())
    }, [editClient, newContacts, ogClient, setCanSave])

    return <Box display="flex" flexDirection="column" gap={3}>
            <Card>
                <Box display="flex" flexDirection="column" gap={3} padding={3}>
                    {(ogClient.code === undefined || ogClient.code.length === 0) &&
                        <TextField 
                            label="Code"
                            variant="outlined"
                            value={editClient?.code}
                            error={(editClient?.code ?? '').length === 0}                            
                            onChange={(e) => {
                                const cleaned = e.currentTarget.value.replace(/\s+/g, '').toUpperCase();
                                setEditClient({
                                    ...editClient,
                                    code: cleaned
                                } as Client)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === ' ' || e.key === 'Spacebar') {
                                    e.preventDefault();
                                }
                            }}
                            inputProps={{ style: { textTransform: "uppercase" } }}
                            required
                        />
                    }
                    <TextField 
                        label="Name"
                        variant="outlined"
                        value={editClient?.name}
                        error={(editClient?.name ?? '').length === 0}
                        onChange={(e) => {
                            setEditClient({
                                ...editClient,
                                name: e.currentTarget.value
                            } as Client)
                        }}
                        required
                    />
                    <TextField 
                        label="ABN"
                        variant="outlined"
                        value={editClient?.abn ?? ''}
                        onChange={(e) => {
                            setEditClient({
                                ...editClient,
                                abn: e.currentTarget.value
                            } as Client)
                        }}
                    />
                    <TextField 
                        label="Address line 1"
                        variant="outlined"
                        value={editClient?.addressLine1 ?? ''}
                        onChange={(e) => {
                            setEditClient({
                                ...editClient,
                                addressLine1: e.currentTarget.value
                            } as Client)
                        }}
                    />

                    <TextField                             
                            label="Address line 2"
                            variant="outlined"
                            value={editClient?.addressLine2 ?? ''}
                            onChange={(e) => {
                                setEditClient({
                                    ...editClient,
                                    addressLine2: e.currentTarget.value
                                } as Client)
                            }}
                        />
                
                    <Box display="flex" gap={3}>
                        <TextField 
                            className="w-100"
                            label="Address line 3"
                            variant="outlined"
                            value={editClient?.addressLine3 ?? ''}
                            onChange={(e) => {
                                setEditClient({
                                    ...editClient,
                                    addressLine3: e.currentTarget.value
                                } as Client)
                            }}
                        />
                                                           
                        <TextField 
                            className="w-25"
                            label="Postcode"
                            variant="outlined"
                            value={editClient?.postCode ?? ''}
                            onChange={(e) => {
                                setEditClient({
                                    ...editClient,
                                    postCode: e.currentTarget.value
                                } as Client)
                            }}
                        />
                    </Box>
                </Box>
            </Card>
            <Box display="flex" justifyContent={"space-between"}>
                <h3>Contacts</h3>
                <Chip color="primary" label={"+"} onClick={(e) => setNewContacts([...newContacts, {clientContactId: getNewContactId() , name: "", email: "", type: ClientContactType.Other, mobile: "", phone: ""}])}/>
            </Box>
            {editClient?.contacts && [...editClient.contacts].sort((a,b) => a.type - b.type).map((contact: ClientContact) => {
                return <ClientContactEntry 
                    key={contact.clientContactId}
                    contact={contact} 
                    contacts={editClient.contacts} 
                    handleChange={
                        (clientContacts: ClientContact[]) => {
                            setEditClient({
                                ...editClient,
                                contacts: clientContacts
                            })
                        }
                    } />
            })}

            {newContacts.map((contact: ClientContact, index: number) => {
                return <ClientContactEntry 
                    key={"newCC-" + index}
                    contact={contact}
                    contacts={newContacts}
                    handleChange={
                        (clientContacts: ClientContact[]) => {
                            setNewContacts([
                                ...clientContacts
                            ])
                        }
                    }
                />
            })}

            {editClient?.contacts.length === 0 && newContacts.length === 0 &&
                <Typography variant="subtitle1" textAlign={"center"} color={"#ABABAB"}>No contacts for selected client</Typography>
            }
        </Box>

}

export default EditClientForm;